// packages/core/src/learning/core-modules/long-term-investing-basics.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 07: "Long-Term Investing Basics" (tier: growth)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Lesson 5 uses openSimulator with scenarioType 'custom' (a valid ScenarioKind).
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'long-term-investing-basics--long-term-investing-means-ownership',
	title: 'Long-term investing means ownership',
	description:
		'You learn that long-term investing is not gambling on numbers blinking on a screen; it is buying exposure to real ownership, risk, and future growth.',
	pillar: 'long-term-investing',
	schoolsOfThought: ['boglehead-indexing', 'efficient-market', 'fi-math'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['ownership', 'long-term-investing', 'beginner-investing'],
	prerequisites: [],
	relatedLessons: [
		'long-term-investing-basics--diversification-protects-you-from-one-big-bet',
	],
	concepts: ['ownership', 'long-term-growth', 'market-participation'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Long-term investing starts with one belief shift: you are not just watching prices move. You are trying to own a tiny piece of future economic activity. That could mean ownership in businesses, broad markets, or assets that may grow or produce value over time.',
		},
		{
			type: 'paragraph',
			text: 'This matters because beginners often treat investing like a scoreboard. Green number means smart. Red number means dumb. But ownership does not work that cleanly. A business, market, or asset can have real value while the price moves around in the short run.',
		},
		{
			type: 'callout',
			icon: '🧠',
			text: 'The screen shows price. Long-term investing asks what you are actually exposed to.',
		},
		{
			type: 'example',
			label: 'Screen vs ownership',
			text: 'Imagine someone invests $200 into a broad long-term portfolio. A month later, it is down to $185. If they only see a score, they feel like they lost. If they understand ownership, they ask better questions: What does this money own? What timeline is this for? Did anything about my long-term reason change, or did the price simply move?',
		},
		{
			type: 'paragraph',
			text: 'That does not mean every investment is good. It means the first lesson is to stop treating investing like a mystery machine. Investing is exposure. You are exposed to possible growth, but also exposed to loss, uncertainty, bad timing, and human emotion.',
		},
		{
			type: 'bullet_list',
			items: [
				'Price is what shows today',
				'Ownership is what you are exposed to',
				'Risk is the cost of possible growth',
				'Timeline decides whether movement is tolerable',
			],
		},
		{
			type: 'paragraph',
			text: 'Long-term investors usually care less about guessing every short-term move and more about staying connected to a sensible process. They understand that money needed soon should not be treated the same as money meant for years from now.',
		},
		{
			type: 'paragraph',
			text: 'The practical change is simple: before you think “is this up or down?” ask “what does this money own, and what job does it have?” That question makes you calmer, sharper, and less likely to confuse movement with meaning.',
		},
		{
			type: 'key_takeaway',
			text: 'Long-term investing is not chasing blinking numbers; it is choosing what future you want your money exposed to.',
		},
	],
	audioScript:
		'Long-term investing gets easier to understand when you stop seeing it as a casino screen. The numbers move, yes. Sometimes they move fast. Sometimes they make you feel smart. Sometimes they make you feel stupid. But the number is not the whole story. The better question is: what do you actually own? When you invest for the long term, you are usually trying to give your money exposure to real economic activity. Businesses, markets, assets, growth, income, risk, uncertainty. You are not promised a smooth ride. You are not promised a specific result. But you are choosing to put money somewhere it can participate in something bigger than cash sitting still. That is the ownership frame. It matters because beginners often panic when the number drops. They think a red screen automatically means they made a bad decision. Maybe they did. But maybe the price just moved. Long-term thinking asks better questions. What is this money for? How long can it stay invested? What risk did I accept? Does this still match the purpose of the money? That is a stronger way to think than refreshing the app and letting your mood follow the chart. The goal is not to become fearless. The goal is to become honest about what investing is. It is ownership plus uncertainty, with the possibility of long-term growth.',
	flashcards: [
		{
			id: 'long-term-investing-basics--long-term-investing-means-ownership--fc-main',
			role: 'main_idea',
			front: 'What is ownership thinking?',
			back: 'Seeing investing as exposure to real assets or markets, not just numbers moving on a screen.',
		},
		{
			id: 'long-term-investing-basics--long-term-investing-means-ownership--fc-watch',
			role: 'watch_out',
			front: 'What is the scoreboard trap?',
			back: 'Treating every short-term price move as proof you are smart or dumb.',
		},
		{
			id: 'long-term-investing-basics--long-term-investing-means-ownership--fc-use',
			role: 'use_it',
			front: 'What should you ask first?',
			back: 'Ask what the money owns and what job that money has.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Name what the money owns',
		prompt: 'Write one sentence describing what your long-term investing money is meant to own or be exposed to.',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'long-term-investing-basics--diversification-protects-you-from-one-big-bet',
	title: 'Diversification protects you from one big bet',
	description:
		'You learn why spreading risk matters and why one exciting idea should not control your whole future.',
	pillar: 'long-term-investing',
	schoolsOfThought: [
		'boglehead-indexing',
		'risk-management',
		'efficient-market',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['diversification', 'risk', 'portfolio-basics'],
	prerequisites: [
		'long-term-investing-basics--long-term-investing-means-ownership',
	],
	relatedLessons: [
		'long-term-investing-basics--costs-quietly-eat-your-future',
	],
	concepts: ['diversification', 'concentration-risk', 'risk-spreading'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Diversification is the boring idea that can save people from very exciting mistakes. It means not letting one company, one trend, one asset, one boss, one city, or one belief control your whole financial future.',
		},
		{
			type: 'paragraph',
			text: 'In investing, diversification spreads risk. Instead of needing one pick to be right, you build exposure across many pieces. Some may disappoint. Some may surprise you. The point is that your whole future does not depend on one bet behaving perfectly.',
		},
		{
			type: 'callout',
			icon: '🛡️',
			text: 'Diversification is not about looking clever. It is about refusing to let one wrong call wreck the plan.',
		},
		{
			type: 'example',
			label: 'One bet vs many',
			text: 'Imagine two beginners. One puts every long-term dollar into one exciting company because everyone online is talking about it. Another spreads money across a broader mix. If the exciting company struggles, the first person feels the whole hit. The second person may still lose money during bad markets, but one company does not control everything.',
		},
		{
			type: 'paragraph',
			text: 'This does not mean diversification removes risk. A diversified portfolio can still go down. A broad market can still have bad years. The honest promise is not “you cannot lose.” The honest benefit is that you are reducing the damage one specific failure can do.',
		},
		{
			type: 'bullet_list',
			items: [
				'One bet can feel exciting',
				'Many bets can feel boring',
				'Boring can be protective',
				'Protection matters when life already has enough risk',
			],
		},
		{
			type: 'paragraph',
			text: 'Diversification is also bigger than investing. If your job, income, savings, housing, and investments all depend on the same fragile situation, your life is concentrated. Long-term money should help create options, not add another hidden dependency.',
		},
		{
			type: 'paragraph',
			text: 'The common trap is confusing confidence with safety. You can feel very confident and still be wrong. Diversification is humility turned into a system. It admits that the future is messy, and it builds a plan that does not require perfect guessing.',
		},
		{
			type: 'key_takeaway',
			text: 'Diversification does not make risk disappear; it keeps one mistake from owning too much of your future.',
		},
	],
	audioScript:
		'Diversification is one of those ideas that sounds boring until you need it. It basically means this: do not let one bet control your whole future. One company. One trend. One asset. One hot tip. One belief. One person online who sounds confident. The world is too messy for your entire plan to depend on one thing going perfectly. In investing, diversification spreads risk across many pieces. That does not mean you cannot lose money. Broad markets can fall. Diversified portfolios can have bad years. But diversification helps protect you from one specific failure destroying the plan by itself. The deeper lesson is humility. You can be smart and still be wrong. You can research something and still miss a risk. You can feel confident because a chart went up, and still be standing on a fragile bet. Diversification is not flashy. It will not make you feel like a genius at a party. But it may keep you from needing to be a genius in the first place. And honestly, that is the point. Long-term investing should not require perfect prediction. It should give your future self a better chance by not concentrating all your hope in one place. The practical question is not “what is the most exciting thing I can own?” It is “am I depending too much on one thing?”',
	flashcards: [
		{
			id: 'long-term-investing-basics--diversification-protects-you-from-one-big-bet--fc-main',
			role: 'main_idea',
			front: 'What is diversification?',
			back: 'Spreading exposure so one company, asset, or idea does not control the whole plan.',
		},
		{
			id: 'long-term-investing-basics--diversification-protects-you-from-one-big-bet--fc-watch',
			role: 'watch_out',
			front: 'What is concentration risk?',
			back: 'Letting too much of your future depend on one bet working perfectly.',
		},
		{
			id: 'long-term-investing-basics--diversification-protects-you-from-one-big-bet--fc-use',
			role: 'use_it',
			front: 'How do you apply it?',
			back: 'Look for places where one failure could hurt too much of your money life.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Spot one big bet',
		prompt: 'Write one area where your money life may depend too much on one company, income source, asset, or belief.',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'long-term-investing-basics--costs-quietly-eat-your-future',
	title: 'Costs quietly eat your future',
	description:
		'You learn why investing costs matter because every dollar lost to friction is a dollar that cannot keep compounding for you.',
	pillar: 'long-term-investing',
	schoolsOfThought: [
		'boglehead-indexing',
		'fi-math',
		'personal-finance-basics',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['fees', 'investing-costs', 'long-term-returns'],
	prerequisites: [
		'long-term-investing-basics--diversification-protects-you-from-one-big-bet',
	],
	relatedLessons: [
		'long-term-investing-basics--behavior-is-part-of-the-portfolio',
	],
	concepts: ['fees', 'friction', 'net-return'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Investing costs are dangerous because they do not always feel dramatic. A fee, spread, subscription, transaction cost, or expensive product can look small in the moment. But long-term investing is a game where small leaks can repeat for years.',
		},
		{
			type: 'paragraph',
			text: 'The important number is not just what an investment earns. It is what you keep after costs. A portfolio can have a decent return on paper, while the user quietly gives up part of that return through fees and friction.',
		},
		{
			type: 'callout',
			icon: '🕳️',
			text: 'A small cost repeated for decades is not small. It is a leak in the compounding machine.',
		},
		{
			type: 'example',
			label: 'The quiet drag',
			text: 'Imagine two people invest the same amount for many years and earn the same before-cost return. One pays very little in ongoing costs. The other pays more every year. The gap may look tiny at first, but over time the higher-cost path has less money left to compound. The cost did not just take dollars once. It took away future growth on those dollars too.',
		},
		{
			type: 'paragraph',
			text: 'This is why long-term investors pay attention to friction. Not because every fee is evil. Some services or products may provide value. But the user should understand the tradeoff. If something costs more, what exactly are you getting in return? Is it education, convenience, planning, access, behavior support, or just a shiny wrapper?',
		},
		{
			type: 'bullet_list',
			items: [
				'Gross return is before costs',
				'Net return is what you keep',
				'Friction compounds too',
				'Unclear fees deserve attention',
			],
		},
		{
			type: 'paragraph',
			text: 'The common mistake is caring about exciting return guesses while ignoring boring cost facts. People will debate a possible extra percent of performance while ignoring a real percent of cost. That is backwards. The uncertain thing gets all the attention. The known drag hides in plain sight.',
		},
		{
			type: 'paragraph',
			text: 'You do not need to become obsessed. You need the basic instinct: long-term money should not bleed quietly because you never looked at the cost of the container, product, platform, or behavior around it.',
		},
		{
			type: 'key_takeaway',
			text: 'Costs matter because the money lost to friction cannot compound for your future self.',
		},
	],
	audioScript:
		'Costs are one of the least exciting parts of investing, which is exactly why they are dangerous. Nobody wants to talk about expense ratios, fees, spreads, subscriptions, trading costs, or friction. It feels small. It feels boring. But long-term investing gives boring things a lot of time to become important. A cost is not just money gone today. It is money that cannot compound tomorrow. That is the part people miss. Imagine two people invest the same amount and get the same before-cost result. One keeps more because their costs are lower. The other loses more to friction every year. At first, the difference may not look huge. But years later, the gap can matter because the higher-cost path had less money working the whole time. This does not mean every cost is bad. Sometimes you pay for real help, convenience, planning, coaching, or behavior support. Fine. But you should know what the cost is and what it gives back. The trap is chasing exciting performance promises while ignoring a boring, real drag. You cannot control the market. You can pay attention to costs. That is the lesson. Long-term investing is not just about what you earn. It is about what you keep, and what keeps working for you.',
	flashcards: [
		{
			id: 'long-term-investing-basics--costs-quietly-eat-your-future--fc-main',
			role: 'main_idea',
			front: 'Why do costs matter?',
			back: 'Costs reduce what stays invested and what can keep compounding over time.',
		},
		{
			id: 'long-term-investing-basics--costs-quietly-eat-your-future--fc-watch',
			role: 'watch_out',
			front: 'What is the hidden drag?',
			back: 'Small repeated fees or friction that quietly reduce long-term results.',
		},
		{
			id: 'long-term-investing-basics--costs-quietly-eat-your-future--fc-use',
			role: 'use_it',
			front: 'What should you check?',
			back: 'Ask what costs exist and what value, if any, they provide in return.',
		},
	],
	applyAction: {
		type: 'runFreedomCost',
		label: 'Test a $25 monthly drag',
		amount: 25,
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'long-term-investing-basics--behavior-is-part-of-the-portfolio',
	title: 'Behavior is part of the portfolio',
	description:
		'You learn that your reactions, panic, boredom, and overconfidence can matter as much as the investments themselves.',
	pillar: 'long-term-investing',
	schoolsOfThought: [
		'behavioral-finance',
		'money-psychology-housel',
		'boglehead-indexing',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['behavior', 'volatility', 'investor-psychology'],
	prerequisites: [
		'long-term-investing-basics--costs-quietly-eat-your-future',
	],
	relatedLessons: [
		'long-term-investing-basics--match-investment-to-timeline',
	],
	concepts: ['investor-behavior', 'panic-selling', 'overconfidence'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Your portfolio is not just what you own. It is also how you behave while owning it. Two people can hold the same investment and get different real-life results because one stays calm and the other keeps reacting.',
		},
		{
			type: 'paragraph',
			text: 'Long-term investing tests patience, boredom, fear, greed, and ego. The hard part is not understanding that prices move. The hard part is staying reasonable when the movement hits your own money.',
		},
		{
			type: 'callout',
			icon: '🧘',
			text: 'A good plan you abandon at the worst moment may behave like a bad plan.',
		},
		{
			type: 'example',
			label: 'Same portfolio, different behavior',
			text: 'Imagine two people both invest $200 a month. During a market drop, one keeps the long-term purpose in mind and reviews calmly. The other panics, stops, sells, restarts later, then chases the next hot idea. Same starting habit. Very different behavior path.',
		},
		{
			type: 'paragraph',
			text: 'The most dangerous emotions are not always obvious. Fear can make you sell when prices are down. Greed can make you chase something after it already ran up. Boredom can make you abandon a simple plan because it does not feel exciting enough. Overconfidence can convince you that normal risk warnings do not apply to you.',
		},
		{
			type: 'bullet_list',
			items: [
				'Fear says run',
				'Greed says chase',
				'Boredom says tinker',
				'Ego says you are different',
			],
		},
		{
			type: 'paragraph',
			text: 'This is why long-term investing needs rules before emotions show up. What will you do if your account drops? How often will you check? What money is not allowed to be invested because it is needed soon? What would make you pause and review instead of react?',
		},
		{
			type: 'paragraph',
			text: 'The goal is not to become emotionless. That is fake. The goal is to design around your normal human reactions. A simple plan that respects your behavior is stronger than a clever plan that assumes you will act like a robot.',
		},
		{
			type: 'key_takeaway',
			text: 'Your behavior is part of your investing result, so build a plan your real self can survive.',
		},
	],
	audioScript:
		'Your behavior is part of the portfolio. That sounds simple, but it is one of the biggest investing lessons. Two people can own the same thing and end up with different results because they behave differently. One person stays consistent. The other panics, sells, restarts, chases, checks constantly, and turns every headline into a decision. The investment mattered, but the behavior mattered too. Long-term investing is emotionally harder than it looks because the test does not happen in a calm classroom. It happens when your own balance is down. It happens when everyone online sounds confident. It happens when your simple plan feels boring and some new thing looks exciting. Fear says run. Greed says chase. Boredom says tinker. Ego says you are different. That is why you need rules before emotions show up. What is this money for? What timeline does it have? How often will you review? What amount of movement would make you nervous? What money should never be invested because you need it soon? These are not glamorous questions, but they protect you from yourself. The goal is not to be emotionless. You are human. The goal is to build a plan that still works when you are human. A plan your real self can follow is better than a perfect-looking plan your stressed self will abandon.',
	flashcards: [
		{
			id: 'long-term-investing-basics--behavior-is-part-of-the-portfolio--fc-main',
			role: 'main_idea',
			front: 'Why does behavior matter?',
			back: 'Your reactions can change your real result, even if the investment itself is unchanged.',
		},
		{
			id: 'long-term-investing-basics--behavior-is-part-of-the-portfolio--fc-watch',
			role: 'watch_out',
			front: 'What emotions hurt investors?',
			back: 'Fear, greed, boredom, and overconfidence can all push bad reactions.',
		},
		{
			id: 'long-term-investing-basics--behavior-is-part-of-the-portfolio--fc-use',
			role: 'use_it',
			front: 'What should you build early?',
			back: 'Rules for drops, checking, timelines, and which money should not be invested.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Write your panic rule',
		prompt: 'Write one rule for what you will do before reacting to a scary market drop.',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'long-term-investing-basics--match-investment-to-timeline',
	title: 'Match the investment to the timeline',
	description:
		'You learn why money needed soon, money needed later, and money meant for freedom should not all be treated the same.',
	pillar: 'long-term-investing',
	schoolsOfThought: ['risk-management', 'fi-math', 'life-design'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['timeline', 'asset-location', 'risk-management'],
	prerequisites: [
		'long-term-investing-basics--behavior-is-part-of-the-portfolio',
	],
	relatedLessons: [
		'long-term-investing-basics--long-term-investing-means-ownership',
	],
	concepts: ['time-horizon', 'money-jobs', 'risk-capacity'],
	blocks: [
		{
			type: 'paragraph',
			text: 'The same dollar can need different treatment depending on when you need it. Money for next month’s rent has a different job than money for a goal ten years away. Long-term investing gets messy when people ignore the timeline.',
		},
		{
			type: 'paragraph',
			text: 'A timeline tells you how much uncertainty the money can handle. If you need the money soon, a market drop is not just annoying. It can break the plan. If the money is for many years from now, short-term movement may be more tolerable.',
		},
		{
			type: 'callout',
			icon: '🗓️',
			text: 'Risk is not only about the investment. It is about when you need the money back.',
		},
		{
			type: 'example',
			label: 'Same investment, wrong job',
			text: 'Imagine someone invests money they need for a car repair fund next month. If the market drops, they may be forced to sell at a bad time. Now compare that with money meant for a long-term freedom goal. A drop still feels bad, but the user may have time to wait, review, and avoid turning movement into a forced loss.',
		},
		{
			type: 'bullet_list',
			items: [
				'Short-term money needs reliability',
				'Medium-term money needs balance',
				'Long-term money can usually accept more movement',
				'Emergency money should not be treated like growth money',
			],
		},
		{
			type: 'paragraph',
			text: 'This is not about making one universal rule for everyone. It is about matching the job to the tool. A hammer is useful, but not for every problem. Investing can be useful, but not for every dollar.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is asking, “What has the highest possible return?” before asking, “When do I need this money?” That question order can hurt people. A high-growth choice can be wrong for short-term money. A very safe choice can be too limited for long-term growth goals. The timeline gives context.',
		},
		{
			type: 'paragraph',
			text: 'This is where money becomes options. When each dollar has the right job, you are less likely to raid the future for today or risk today’s stability for future growth. You build a cleaner system: safety money, opportunity money, and long-term freedom money.',
		},
		{
			type: 'key_takeaway',
			text: 'Match money to its timeline before you decide how much risk it should carry.',
		},
	],
	audioScript:
		'Every dollar needs a job, and every job has a timeline. This is where a lot of investing mistakes come from. People ask, “What can grow the most?” before they ask, “When do I need this money?” That order is dangerous. Money you need soon cannot handle the same uncertainty as money you will not touch for years. If your rent money, emergency money, or short-term repair money drops at the wrong time, the problem is not just emotional. You may be forced to sell or scramble. But money meant for a long-term goal can usually tolerate more movement because it has time. That does not make it risk-free. It just changes the context. Timeline is what tells you whether volatility is annoying or dangerous. The goal is not to make one rule for every person. The goal is to match the tool to the job. Some money is there to protect you. Some money is there to create opportunity. Some money is there to pursue future freedom. When you mix those jobs, your system gets stressful. When you separate them, you make better decisions. You do not need to predict everything. You need to respect what each dollar is supposed to do and when you need it back.',
	flashcards: [
		{
			id: 'long-term-investing-basics--match-investment-to-timeline--fc-main',
			role: 'main_idea',
			front: 'What does timeline decide?',
			back: 'How much uncertainty the money can reasonably handle before you need it.',
		},
		{
			id: 'long-term-investing-basics--match-investment-to-timeline--fc-watch',
			role: 'watch_out',
			front: 'What is the wrong first question?',
			back: 'Asking for the highest possible return before asking when the money is needed.',
		},
		{
			id: 'long-term-investing-basics--match-investment-to-timeline--fc-use',
			role: 'use_it',
			front: 'How do you sort dollars?',
			back: 'Separate safety money, opportunity money, and long-term freedom money by timeline.',
		},
	],
	applyAction: {
		type: 'openSimulator',
		label: 'Test a long-term plan',
		scenarioType: 'custom',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'long-term-investing-basics',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--cq--q1',
			question: 'What is the main belief shift in long-term investing?',
			options: [
				'Investing is only about short-term price movement',
				'Investing is exposure to ownership, risk, and possible future growth',
				'Investing removes uncertainty',
				'Investing should be judged every day',
			],
			correctIndex: 1,
			explanation:
				'Long-term investing is about what the money owns or is exposed to, not only what the price does today.',
		},
		{
			type: 'true_false',
			id: 'long-term-investing-basics--cq--q2',
			question:
				'A red number on the screen automatically means the investment decision was wrong.',
			correct: false,
			explanation:
				'A drop may reflect normal price movement. The better questions are what the money owns, what timeline it has, and what risk was accepted.',
		},
		{
			type: 'scenario',
			id: 'long-term-investing-basics--cq--q3',
			scenario:
				'Sam puts all long-term money into one exciting company because a creator online sounds confident.',
			question: 'What risk is Sam taking?',
			options: [
				'Diversification',
				'Concentration risk',
				'Emergency planning',
				'Cost control',
			],
			correctIndex: 1,
			explanation:
				'Sam is letting one company control too much of the plan. That is concentration risk.',
		},
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--cq--q4',
			question: 'What does diversification mainly protect against?',
			options: [
				'All possible losses',
				'Every market downturn',
				'One specific bet hurting too much of the plan',
				'The need to understand risk',
			],
			correctIndex: 2,
			explanation:
				'Diversification does not remove risk. It reduces dependence on one specific company, asset, or idea.',
		},
		{
			type: 'true_false',
			id: 'long-term-investing-basics--cq--q5',
			question:
				'Costs matter because money lost to fees or friction cannot keep compounding.',
			correct: true,
			explanation:
				'Costs reduce what stays invested, and that can reduce future growth on those dollars too.',
		},
		{
			type: 'scenario',
			id: 'long-term-investing-basics--cq--q6',
			scenario:
				'Two people earn the same before-cost return, but one pays higher ongoing costs every year.',
			question: 'What is the likely long-term issue?',
			options: [
				'The higher-cost person always gets a better result',
				'Costs may quietly reduce what the person keeps',
				'Fees only matter for rich people',
				'Costs remove all investing risk',
			],
			correctIndex: 1,
			explanation:
				'Higher ongoing costs can reduce net returns and leave less money compounding.',
		},
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--cq--q7',
			question:
				'What is the better first question before judging an investment account?',
			options: [
				'Why is it not always green?',
				'What does this money own and what job does it have?',
				'Which creator is most confident?',
				'How do I avoid all risk?',
			],
			correctIndex: 1,
			explanation:
				'Ownership and purpose give context. A short-term price move alone is not enough.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--final--q1',
			question: 'What does long-term investing mean in this module?',
			options: [
				'Predicting short-term prices',
				'Choosing exposure to ownership, risk, and possible future growth',
				'Avoiding all market movement',
				'Buying whatever recently went up',
			],
			correctIndex: 1,
			explanation:
				'The module frames long-term investing as ownership and exposure, not prediction or trend chasing.',
		},
		{
			type: 'scenario',
			id: 'long-term-investing-basics--final--q2',
			scenario:
				'Alina checks her account daily and feels successful only when it is green.',
			question: 'What trap is she in?',
			options: [
				'The scoreboard trap',
				'Diversification',
				'Timeline matching',
				'Cost analysis',
			],
			correctIndex: 0,
			explanation:
				'The scoreboard trap treats every short-term move as proof of success or failure.',
		},
		{
			type: 'true_false',
			id: 'long-term-investing-basics--final--q3',
			question:
				'Diversification can reduce the damage from one wrong call, but it cannot remove all risk.',
			correct: true,
			explanation:
				'Diversification spreads risk, but broad investments can still decline.',
		},
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--final--q4',
			question: 'What is concentration risk?',
			options: [
				'Having no money invested',
				'Depending too much on one company, asset, income source, or belief',
				'Paying attention to costs',
				'Matching money to a timeline',
			],
			correctIndex: 1,
			explanation:
				'Concentration risk is when too much of the plan depends on one thing working.',
		},
		{
			type: 'scenario',
			id: 'long-term-investing-basics--final--q5',
			scenario:
				'Marcus ignores a 1% ongoing cost because it feels small, but he plans to invest for decades.',
			question: 'What should he understand?',
			options: [
				'Small repeated costs can become meaningful over long periods',
				'Costs never matter',
				'Higher costs always mean higher value',
				'Long timelines erase all fees',
			],
			correctIndex: 0,
			explanation:
				'Repeated costs can reduce what stays invested and what can keep compounding.',
		},
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--final--q6',
			question: 'What is net return?',
			options: [
				'Return before costs',
				'What you keep after costs and friction',
				'A promised result',
				'A short-term account change',
			],
			correctIndex: 1,
			explanation:
				'Net return is what remains after costs. That is what actually works for the investor.',
		},
		{
			type: 'true_false',
			id: 'long-term-investing-basics--final--q7',
			question:
				'A good-looking plan can still fail the user if they abandon it during fear or excitement.',
			correct: true,
			explanation:
				'Behavior matters. A plan only helps if the user can realistically follow it through normal stress.',
		},
		{
			type: 'scenario',
			id: 'long-term-investing-basics--final--q8',
			scenario:
				'During a market drop, Taylor sells immediately, then buys back later after prices rise because it feels safe again.',
			question: 'Which lesson does this connect to most?',
			options: [
				'Behavior is part of the portfolio',
				'Costs never matter',
				'Ownership removes risk',
				'Diversification guarantees gains',
			],
			correctIndex: 0,
			explanation:
				'Fear-based reactions can change real outcomes. Investor behavior is part of the result.',
		},
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--final--q9',
			question: 'What does timeline help decide?',
			options: [
				'How much uncertainty the money can handle',
				'Whether money is real',
				'Whether investing is guaranteed',
				'Which hot tip is best',
			],
			correctIndex: 0,
			explanation:
				'The timeline matters because money needed soon usually cannot handle the same movement as long-term money.',
		},
		{
			type: 'scenario',
			id: 'long-term-investing-basics--final--q10',
			scenario:
				'Nina invests money she needs for rent in two months because she wants it to grow faster.',
			question: 'What is the core problem?',
			options: [
				'The money’s job and timeline do not match the risk',
				'She diversified too much',
				'Costs are impossible to understand',
				'Long-term investing always works in two months',
			],
			correctIndex: 0,
			explanation:
				'Short-term required money usually needs reliability. Risky movement can create a real-life problem.',
		},
		{
			type: 'true_false',
			id: 'long-term-investing-basics--final--q11',
			question:
				'Nisk should teach long-term investing as education and tradeoff testing, not as instructions on what to buy.',
			correct: true,
			explanation:
				'The product posture is educational. It teaches concepts, risks, and scenarios without prescribing individual investment decisions.',
		},
		{
			type: 'multiple_choice',
			id: 'long-term-investing-basics--final--q12',
			question:
				'Which combination best summarizes what a long-term investor can actually control?',
			options: [
				'The exact market return each year',
				'Behavior, costs, diversification, and matching money to its timeline',
				'When the next downturn arrives',
				'Which single company will win',
			],
			correctIndex: 1,
			explanation:
				'You cannot control returns, but you can manage behavior, costs, diversification, and timeline — the levers this module teaches.',
		},
	],
};

export const LONG_TERM_INVESTING_BASICS_MODULE: LearningModule = {
	id: 'long-term-investing-basics',
	title: 'Long-Term Investing Basics',
	description:
		'Long-term investing is ownership, not a scoreboard. Learn the levers you actually control — diversification, costs, your own behavior, and matching each dollar to its timeline — instead of chasing blinking numbers.',
	emoji: '📈',
	accentColor: '#c8ff00',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 7,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
